import { useRef, useState } from 'react';
import { ELSButton, ELSIcon } from 'components/common/els';
import { EDCollectionConfig } from './constants';
import { EDRow } from './EmergencyDepartment';

export interface AddEntryModalProps {
  config: EDCollectionConfig;
  initialRows: EDRow[];
  onCommit: (rows: EDRow[]) => void;
  onClose: VoidFunction;
}

const AddEntryModal = ({ config, initialRows, onCommit, onClose }: AddEntryModalProps) => {
  const [rows, setRows] = useState<EDRow[]>(initialRows);
  const [draft, setDraft] = useState<EDRow>({});
  // Snapshot the rows the modal opened with; the prop can be a fresh array on every parent render.
  const openedWithRows = useRef<EDRow[]>(initialRows);

  const setDraftValue = (field: string, value: string) => setDraft((prev) => ({ ...prev, [field]: value }));

  // No column is mandatory, so a single filled cell is enough to add — and to reset.
  const isDraftDirty = config.columns.some((column) => Boolean(String(draft[column.key] ?? '').trim()));

  const handleAddDraft = () => {
    if (!isDraftDirty) return;
    setRows((prev) => [...prev, draft]);
    setDraft({});
  };

  const handleResetDraft = () => setDraft({});
  const handleDeleteRow = (rowIndex: number) => setRows((prev) => prev.filter((_, index) => index !== rowIndex));

  const hasPendingRowChanges = rows !== openedWithRows.current;
  const hasSomethingToSave = rows.length > 0 || hasPendingRowChanges;

  const handleSave = () => {
    onCommit(rows);
    onClose();
  };

  return (
    <div className="emergency-department__modal-backdrop" role="dialog" aria-modal="true" aria-label={config.modalTitle}>
      <div className="emergency-department__modal">
        <div className="emergency-department__modal-header">
          <h3>{config.modalTitle}</h3>
        </div>

        <div className="emergency-department__modal-form">
          <div className={`emergency-department__modal-fields emergency-department__modal-fields--${config.key}`}>
            {config.columns.map((column) => (
              <div className="emergency-department__modal-field" key={column.key}>
                <span className="c-els-field__label">{column.label}</span>
                <input
                  className="c-els-field__input"
                  type="text"
                  value={draft[column.key] ?? ''}
                  maxLength={150}
                  onChange={(event) => setDraftValue(column.key, event.target.value)}
                  aria-label={column.label}
                />
              </div>
            ))}
          </div>

          <div className="emergency-department__modal-actions">
            <ELSButton text="Add" isDisabled={!isDraftDirty} onClick={handleAddDraft} />
            <ELSButton text="Reset" type="secondary" isDisabled={!isDraftDirty} onClick={handleResetDraft} />
            {!hasSomethingToSave && <ELSButton text="Cancel" type="secondary" onClick={onClose} />}
          </div>

          {rows.length > 0 && (
            <div className="emergency-department__modal-added">
              <h4>{`Added ${config.title}`}</h4>
              <table className="emergency-department__table emergency-department__table--has-actions">
                <thead>
                  <tr>
                    {config.columns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                    <th className="emergency-department__row-actions" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={rowIndex}>
                      {config.columns.map((column) => (
                        <td key={column.key}>{String(row[column.key] ?? '').trim()}</td>
                      ))}
                      <td className="emergency-department__row-actions">
                        <button className="emergency-department__row-delete" type="button" onClick={() => handleDeleteRow(rowIndex)} aria-label="Delete row">
                          <ELSIcon name="trash" prefix="gizmo" color="secondary" size="1x" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasSomethingToSave && (
            <div className="emergency-department__modal-footer">
              <ELSButton text="Save" onClick={handleSave} />
              <ELSButton text="Cancel" type="secondary" onClick={onClose} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AddEntryModal.displayName = 'AddEntryModal';
export default AddEntryModal;
